import React from "react";

import pdf from "@react-pdf/renderer";

import { Features } from "@/config/features";
import { graphql } from "@/gql";
import { Maybe } from "@/gql/graphql";
import { calculateMacro } from "@/pages/Clients/ClientDetails/ClientDiary/Meals/utils/macros";
import { colors } from "@/theme/overrides/colors";
import { format } from "@/utils/datetime";
import { round } from "@/utils/number";
import { humanize } from "@/utils/string";
import { FragmentType } from "@/utils/type";

import Divider from "./Divider";
import Flex from "./layout/Flex";

const { Text, View, StyleSheet } = pdf;

const styles = StyleSheet.create({
  root: {
    width: "100%",
    margin: "12px 0px",
    fontWeight: "thin",
    fontSize: "10px",
    padding: "12px 0px",
    height: "auto",
  },
  title: {
    fontWeight: "ultrabold",
    fontSize: "12px",
  },
  bold: {
    fontWeight: "bold",
  },
  textSecondary: {
    color: colors.gray,
    fontWeight: "semibold",
  },
  macros: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
  },
  macro: {
    marginTop: "4px",
  },
  rowItem: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    fontWeight: "semibold",
    fontSize: "10px",
    marginBottom: "12px",
  },
  grayBackground: {
    backgroundColor: colors.light,
  },
  recipeName: {
    fontWeight: "bold",
    textDecoration: "underline",
    marginBottom: "6px",
  },
});

type Props = {
  data: FragmentType<typeof sectionFragment>;
  macronutrients?: Maybe<FragmentType<typeof sectionMacronutrientsFragment>>;
  day: number;
};

function isEven(value: number): boolean {
  if (value % 2 == 0) return true;
  else return false;
}

const sectionFragment = graphql(`
  fragment PDFMealPlanSection on PlannedMeal {
    id
    meal
    recommendedTime
    itemsByDate {
      date
      totalEnergyKcal
      totalCarbohydrateGrams
      totalProteinGrams
      totalFatGrams
      recipes {
        uuid
        name
      }
      items {
        id
        amount
        energyKcal
        cellRecipeUuid
        food {
          id
          name
        }
        foodPortion {
          id
          unitWithModifier
        }
      }
    }
  }
`);

const sectionMacronutrientsFragment = graphql(`
  fragment PDFMealPlanSection_Macronutrients on MealPlan {
    id
    macronutrientsCarbohydratePercent
    macronutrientsProteinPercent
    macronutrientsFatPercent
  }
`);

const Section: React.FC<Props> = ({ data, day }) => {
  const itemsForDate = data.itemsByDate[day - 1];

  // Foods grouped under the recipes the coach added to this cell, with the
  // ungrouped (loose) foods rendered first under no heading.
  const looseItems = itemsForDate.items.filter((item) => !item.cellRecipeUuid);
  const recipeGroups = [
    { key: "loose", name: null as string | null, items: looseItems },
    ...itemsForDate.recipes.map((recipe) => ({
      key: recipe.uuid,
      name: recipe.name as string | null,
      items: itemsForDate.items.filter(
        (item) => item.cellRecipeUuid === recipe.uuid
      ),
    })),
  ]
    .filter((group) => group.items.length !== 0)
    .map((group) => ({
      ...group,
      totalEnergyKcal: group.items.reduce(
        (total, item) => total + item.energyKcal,
        0
      ),
    }));

  // Macro grams and percentages reflect the actual foods planned for this
  // meal (server-computed, portion-scaled totals) — matching what the coach
  // sees in the meal planner. Previously these were derived from the plan's
  // target distribution, so the PDF disagreed with the planner whenever the
  // real foods didn't hit the target split.
  const carbohydrateGrams = itemsForDate.totalCarbohydrateGrams;
  const proteinGrams = itemsForDate.totalProteinGrams;
  const fatGrams = itemsForDate.totalFatGrams;
  const macroPercent = calculateMacro(itemsForDate.totalEnergyKcal);
  const carbohydratePercent = macroPercent.carbsPercentage(carbohydrateGrams);
  const proteinPercent = macroPercent.proteinPercentage(proteinGrams);
  const fatPercent = macroPercent.fatsPercentage(fatGrams);

  return (
    <View wrap={false}>
      <View style={styles.root}>
        <Flex
          flexDirection="row"
          justifyContent="spaceBetween"
          alignItems="start"
        >
          <View style={{ width: "30%", height: "100%" }}>
            <Flex
              alignItems="start"
              flexDirection="column"
              justifyContent="spaceBetween"
            >
              <View>
                <Text style={styles.title}>{humanize(data.meal)}</Text>
                <Text style={styles.bold}>
                  {format({ timeofday: data.recommendedTime }, "hh:mm a")}
                </Text>
                <View style={{ marginBottom: "4px" }} />
                <Text style={[styles.bold, styles.textSecondary]}>
                  {`${Math.round(itemsForDate.totalEnergyKcal)} kcal`}
                </Text>
              </View>

              <View style={{ marginBottom: "8px" }} />

              {Features.pdfMacronutrientsEnabled && (
                <View style={styles.macros}>
                  <View>
                    <Flex flexDirection="row" alignItems="center">
                      <Text style={styles.bold}>Carbohydrates:</Text>
                      <View style={{ marginRight: "2px" }} />
                      <Text style={styles.textSecondary}>
                        {`${carbohydratePercent}%`}
                        {`(${round(carbohydrateGrams, 2)}g)`}
                      </Text>
                    </Flex>
                  </View>

                  <View style={styles.macro}>
                    <Flex flexDirection="row" alignItems="center">
                      <Text style={styles.bold}>Protein:</Text>
                      <View style={{ marginRight: "2px" }} />
                      <Text style={styles.textSecondary}>
                        {`${proteinPercent}%`}
                        {`(${round(proteinGrams, 2)}g)`}
                      </Text>
                    </Flex>
                  </View>

                  <View style={styles.macro}>
                    <Flex flexDirection="row" alignItems="center">
                      <Text style={styles.bold}>Fat:</Text>
                      <View style={{ marginRight: "2px" }} />
                      <Text style={styles.textSecondary}>
                        {`${fatPercent}%`}
                        {`(${round(fatGrams, 2)}g)`}
                      </Text>
                    </Flex>
                  </View>
                </View>
              )}
            </Flex>
          </View>

          <View style={{ width: "65%" }}>
            {itemsForDate.items.length !== 0 ? (
              <>
                {recipeGroups.map((group) => (
                  <View key={group.key}>
                    {!!group.name && (
                      <Text style={styles.recipeName}>{`${
                        group.name
                      } (${Math.round(group.totalEnergyKcal)} kcal)`}</Text>
                    )}
                    <View>
                      {group.items.map((item, index) => {
                        const grayBackground = isEven(index + 1)
                          ? styles.grayBackground
                          : styles.rowItem;

                        return (
                          <View
                            style={[styles.rowItem, grayBackground]}
                            key={index}
                          >
                            <View style={{ width: "40%" }}>
                              <Text>{item.food.name}</Text>
                            </View>
                            <View style={{ marginRight: "12px" }} />
                            <View style={{ width: "60%" }}>
                              <View style={{ width: "100%" }}>
                                <Flex
                                  justifyContent="spaceBetween"
                                  flexDirection="row"
                                >
                                  <View
                                    style={{
                                      width: "50%",
                                      alignSelf: "flex-start",
                                    }}
                                  >
                                    <Text
                                      style={{ alignSelf: "flex-start" }}
                                    >{`${item.amount} ${item.foodPortion.unitWithModifier}`}</Text>
                                  </View>
                                  <View style={{ marginRight: "12px" }} />
                                  <View style={{ width: "50%" }}>
                                    <Text
                                      style={{ alignSelf: "flex-end" }}
                                    >{`${Math.round(
                                      item.energyKcal
                                    )} kcal`}</Text>
                                  </View>
                                </Flex>
                              </View>
                            </View>
                          </View>
                        );
                      })}
                    </View>
                  </View>
                ))}
              </>
            ) : (
              <Flex alignItems="center" justifyContent="center">
                <Text>No data</Text>
              </Flex>
            )}
          </View>
        </Flex>
      </View>
      <Divider size="large" />
    </View>
  );
};

export default Section;
